import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Swal from 'sweetalert2';
import { FiArrowLeft, FiMessageCircle, FiSend, FiChevronDown, FiChevronUp, FiExternalLink, FiPlus, FiArrowUp, FiArrowDown, FiTrash2 } from 'react-icons/fi';
import { commonCoreProjects, newCommonCoreProjects, piscineDays, outerCircleProjects } from '../data/projects';
import { API_BASE_URL } from '../config';
import './ProjectDetails.css';

const ProjectDetails = ({ user }) => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [expandedFeedbacks, setExpandedFeedbacks] = useState({});
    
    // Resource Form State
    const [showResourceForm, setShowResourceForm] = useState(false);
    const [newResource, setNewResource] = useState({ title: "", type: "Docs", url: "" });

    // Feedback Form State
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    
    // User List Tab State
    const [activeUserTab, setActiveUserTab] = useState('completed');
    const [newFeedbackText, setNewFeedbackText] = useState("");
    const [newFeedbackType, setNewFeedbackType] = useState("Information");
    const [activeFeedbackFilter, setActiveFeedbackFilter] = useState("All");
 
    // Tab State
    const [activeTab, setActiveTab] = useState('resources');

    useEffect(() => {
        // Find project in either Common Core, Piscine, or Outer Circle lists
        const allProjects = [...newCommonCoreProjects, ...commonCoreProjects, ...piscineDays, ...outerCircleProjects];
        const foundProject = allProjects.find(p => p.title === decodeURIComponent(projectId));
        
        if (foundProject) {
            const projectData = JSON.parse(JSON.stringify(foundProject));
            const slug = projectData.title.toLowerCase().replace(/ /g, '-');
            
            // Define fetch operations
            const fetchFeedback = fetch(`${API_BASE_URL}/projects/${slug}/feedback`)
                .then(res => res.ok ? res.json() : null)
                .catch(() => null);

            const fetchActivity = fetch(`${API_BASE_URL}/projects/${slug}/activity`)
                .then(res => res.ok ? res.json() : { finished: [], in_progress: [] })
                .catch(() => ({ finished: [], in_progress: [] }));

            const fetchResources = fetch(`${API_BASE_URL}/projects/${slug}/resources`)
                .then(res => res.ok ? res.json() : [])
                .catch(() => []);

            // Execute in parallel
            Promise.all([fetchFeedback, fetchActivity, fetchResources]).then(([feedbackData, activityData, resourcesData]) => {
                // Process Feedback
                if (feedbackData && Array.isArray(feedbackData.data)) {
                    const backendFeedbacks = feedbackData.data.map(fb => ({
                        id: fb.id,
                        user: fb.user.login,
                        userImage: fb.user.image_url,
                        date: new Date(fb.created_at).toLocaleDateString(),
                        rating: fb.rating,
                        comment: fb.comment,
                        type: fb.type || 'Information', // Default to Information if undefined
                        votes: fb.votes || 0, // Add votes property
                        replies: fb.replies ? fb.replies.map(r => ({
                            id: r.id, // Ensure ID is mapped
                            user: r.user.login,
                            userImage: r.user.image_url,
                            comment: r.comment,
                            date: new Date(r.created_at).toLocaleDateString()
                        })) : []
                    }));
                    projectData.feedbacks = backendFeedbacks;
                } else {
                    projectData.feedbacks = [];
                }

                // Process Activity
                if (activityData) {
                    const excludedUsers = [
                        "adnan", "dolan", "hamza", "kapitchou", "terabat", 
                        "omi", "mountassir", "trabat", "giuseppe", "kapitcha"
                    ];
                    const filterUsers = (list) => (list || []).filter(u => !excludedUsers.includes(u.user?.toLowerCase()));

                    projectData.completedBy = filterUsers(activityData.finished);
                    projectData.inProgressBy = filterUsers(activityData.in_progress);
                }
                 // Process Resources
                 if (Array.isArray(resourcesData)) {
                    const mappedResources = resourcesData.map(r => ({
                        id: r.id,
                        title: r.title,
                        type: r.type,
                        url: r.url,
                        user: r.user ? r.user.login : 'Anonymous',
                        userImage: r.user ? r.user.image_url : null,
                        date: new Date(r.created_at).toLocaleDateString(),
                        upvotes: r.score || 0,
                        userVote: r.userVote || 0
                    }));
                    // Set resources (overriding initial empty array or adding to it)
                    projectData.resources = mappedResources;
                } else {
                    projectData.resources = [];
                }

                setProject(projectData);
            });
        }
    }, [projectId]);

    const handleReplyClick = (idx) => {
        if (replyingTo === idx) {
            setReplyingTo(null);
        } else {
            setReplyingTo(idx);
            setReplyText("");
        }
    };

    const handleMentionReply = (feedbackIdx, username) => {
        setReplyingTo(feedbackIdx);
        setReplyText(`@${username} `);
    };

    const submitReply = async (idx) => {
        if (!replyText.trim()) return;
        
        const feedbackId = project.feedbacks[idx].id;
        
        // Optimistic Update
        const tempId = Date.now();
        const newReply = {
            id: tempId,
            user: user ? user.login : "Anonymous",
            userImage: user ? user.image_url : null, // Use image_url from user prop
            comment: replyText,
            date: "Just now"
        };

        const originalProject = { ...project };
        const projectCopy = { ...project }; // Deep copy not needed if we only mutate deeper
        if (!projectCopy.feedbacks[idx].replies) {
            projectCopy.feedbacks[idx].replies = [];
        }
        projectCopy.feedbacks[idx].replies.push(newReply);
        
        setProject(projectCopy);
        setReplyText("");
        setReplyingTo(null);
        setExpandedFeedbacks(prev => ({ ...prev, [idx]: true }));
        
        try {
            const res = await fetch(`${API_BASE_URL}/feedback/${feedbackId}/reply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    comment: newReply.comment
                }),
                credentials: 'include'
            });

            if (res.ok) {
                const savedReply = await res.json();
                
                // Update the temp reply with real ID
                setProject(current => {
                    const updated = { ...current };
                    const fb = updated.feedbacks[idx];
                    const replyIndex = fb.replies.findIndex(r => r.id === tempId);
                    if (replyIndex !== -1) {
                         fb.replies[replyIndex].id = savedReply.id;
                    }
                    return updated;
                });
            } else {
                 throw new Error('Failed to save');
            }
        } catch (error) {
            console.error("Failed to submit reply:", error);
            alert("Failed to submit reply. Please try again.");
            setProject(originalProject); // Revert
        }
    };

    const toggleReplies = (idx) => {
        setExpandedFeedbacks(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };
    const handleResourceVote = async (index, type) => {
        if (!user) {
            alert("Please log in to vote.");
            return;
        }

        const originalProject = JSON.parse(JSON.stringify(project)); // Deep copy for revert
        const updatedProject = { ...project };
        const resource = updatedProject.resources[index];
        const slug = project.title.toLowerCase().replace(/ /g, '-');
        
        // Optimistic Update
        const currentVote = resource.userVote || 0;
        let newVote = 0;
        let newUpvotes = resource.upvotes || 0;

        // Toggling off the current vote
        if (currentVote === type) {
            newUpvotes = newUpvotes - currentVote;
            newVote = 0;
        } else {
            // Changing vote (e.g. from 0 to 1, or -1 to 1)
            newUpvotes = newUpvotes - currentVote + type;
            newVote = type;
        }

        resource.upvotes = newUpvotes;
        resource.userVote = newVote;
        setProject(updatedProject);

        try {
            const res = await fetch(`${API_BASE_URL}/projects/${slug}/resources/${resource.id}/vote`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value: type }),
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error("Vote failed");
            }
            // Server response is ignored because we already updated optimistically
            // Unless we want to sync the exact count from server, but simple increment is usually safe enough for now
        } catch (error) {
            console.error("Error voting:", error);
            setProject(originalProject); // Revert on error
        }
    };

    const handleFeedbackVote = (id, type) => {
        const updatedProject = { ...project };
        const fbIndex = updatedProject.feedbacks.findIndex(f => f.id === id);
        
        if (fbIndex !== -1) {
            const feedback = updatedProject.feedbacks[fbIndex];
            const currentUser = user ? user.login : "Anonymous";
            const storageKey = `mintra_votes_${currentUser}`;

            if (!feedback.userVotes) feedback.userVotes = {};
            
            const currentVote = feedback.userVotes[currentUser] || 0;
            let newVote = 0;
            
            if (currentVote === type) {
                feedback.userVotes[currentUser] = 0;
                feedback.votes = (feedback.votes || 0) - type;
                newVote = 0;
            } else {
                feedback.votes = (feedback.votes || 0) - currentVote + type;
                feedback.userVotes[currentUser] = type;
                newVote = type;
            }
            
            // Persist to Local Storage
            const storedVotes = JSON.parse(localStorage.getItem(storageKey) || '{"resources": {}, "feedbacks": {}}');
            if (!storedVotes.feedbacks) storedVotes.feedbacks = {};

            if (newVote === 0) {
                delete storedVotes.feedbacks[feedback.id];
            } else {
                storedVotes.feedbacks[feedback.id] = newVote;
            }
            localStorage.setItem(storageKey, JSON.stringify(storedVotes));
            
            setProject(updatedProject);
        }
    };

    // Custom SweetAlert2 Mixin for Mintra Theme
    const MintraAlert = Swal.mixin({
        customClass: {
            popup: 'mintra-swal-popup',
            title: 'mintra-swal-title',
            content: 'mintra-swal-content',
            confirmButton: 'mintra-swal-confirm',
            cancelButton: 'mintra-swal-cancel'
        },
        buttonsStyling: false,
        background: '#20162d',
        color: '#e9d5ff',
        reverseButtons: true
    });

    const handleDeleteFeedback = async (id) => {
        const result = await MintraAlert.fire({
            title: 'Delete Feedback?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;
        
        const originalProject = JSON.parse(JSON.stringify(project));
        
        // Optimistic Delete
        const updatedProject = { ...project };
        updatedProject.feedbacks = updatedProject.feedbacks.filter(f => f.id !== id);
        setProject(updatedProject);

        try {
            const res = await fetch(`${API_BASE_URL}/feedback/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error("Failed to delete");
            }
        } catch (e) {
            console.error(e);
            setProject(originalProject);
            alert("Failed to delete feedback.");
        }
    };

    const handleDeleteReply = async (feedbackIndex, replyIndex, replyId) => {
        const result = await MintraAlert.fire({
            title: 'Delete Reply?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        const originalProject = JSON.parse(JSON.stringify(project));

        // Optimistic Delete
        const updatedProject = { ...project };
        const feedback = updatedProject.feedbacks[feedbackIndex];
        feedback.replies.splice(replyIndex, 1);
        setProject(updatedProject);

        try {
           const res = await fetch(`${API_BASE_URL}/replies/${replyId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
             
            if (!res.ok) {
                 // Fallback: Try nested endpoint if the first one failed (just in case)
                 const feedbackId = originalProject.feedbacks[feedbackIndex].id;
                 const res2 = await fetch(`${API_BASE_URL}/feedback/${feedbackId}/replies/${replyId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                 });
                 
                 if (!res2.ok) {
                    throw new Error("Failed to delete reply");
                 }
            }
        } catch (e) {
            console.error(e);
            setProject(originalProject);
            alert("Failed to delete reply.");
        }
    };

    const handleDeleteResource = async (resourceIndex, resourceId) => {
        const result = await MintraAlert.fire({
            title: 'Delete Resource?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;
        
        const originalProject = JSON.parse(JSON.stringify(project));
        
        // Optimistic Delete
        const updatedProject = { ...project };
        updatedProject.resources.splice(resourceIndex, 1);
        setProject(updatedProject);

        const slug = project.title.toLowerCase().replace(/ /g, '-');
        try {
            const res = await fetch(`${API_BASE_URL}/projects/${slug}/resources/${resourceId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error("Failed to delete resource");
            }
        } catch (e) {
            console.error(e);
            setProject(originalProject);
            alert("Failed to delete resource.");
        }
    };

    const handleAddResource = async () => {
        if (!newResource.title || !newResource.url) return;
        
        // Optimistic Update
        const tempId = Date.now();
        const resourceToAdd = {
            id: tempId,
            title: newResource.title,
            type: newResource.type,
            url: newResource.url,
            user: user ? user.login : "Anonymous", 
            userImage: user ? user.image_url : null, // Use image_url from user prop
            date: "Just now",
            upvotes: 0
        };

        const originalProject = JSON.parse(JSON.stringify(project));
        const updatedProject = { ...project };
        if (!updatedProject.resources) {
            updatedProject.resources = [];
        }
        updatedProject.resources.unshift(resourceToAdd); // Add to top
        
        setProject(updatedProject);
        setNewResource({ title: "", type: "Docs", url: "" });
        setShowResourceForm(false);

        const slug = project.title.toLowerCase().replace(/ /g, '-');

        try {
            const res = await fetch(`${API_BASE_URL}/projects/${slug}/resources`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newResource),
                credentials: 'include'
            });

            if (res.ok) {
                const savedResource = await res.json();
                
                // Update with real ID
                setProject(current => {
                    const updated = { ...current };
                    const idx = updated.resources.findIndex(r => r.id === tempId);
                    if (idx !== -1) {
                        updated.resources[idx].id = savedResource.id;
                        // If backend returns extra data, update it here
                    }
                    return updated;
                });
            } else {
                 throw new Error("Failed to add resource");
            }
        } catch (error) {
            console.error("Error adding resource:", error);
            setProject(originalProject); // Revert
            alert("Failed to add resource. Please try again.");
        }
    };

    const handleAddFeedback = async () => {
        if (!newFeedbackText.trim()) return;

        // Optimistic Update
        const tempId = Date.now();
        const feedbackToAdd = {
            id: tempId,
            user: user ? user.login : "Anonymous",
            userImage: user ? user.image_url : null, // Use image_url from user prop
            date: "Just now",
            rating: 5,
            comment: newFeedbackText,
            type: newFeedbackType,
            replies: []
        };

        const originalProject = JSON.parse(JSON.stringify(project));
        const updatedProject = { ...project };
        if (!updatedProject.feedbacks) {
            updatedProject.feedbacks = [];
        }
        updatedProject.feedbacks.unshift(feedbackToAdd);
        
        setProject(updatedProject);
        setNewFeedbackText("");
        setNewFeedbackType("Information");
        setShowFeedbackForm(false);
        setActiveFeedbackFilter("All");

        const slug = project.title.toLowerCase().replace(/ /g, '-');

        try {
            const res = await fetch(`${API_BASE_URL}/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    projectSlug: slug,
                    comment: newFeedbackText,
                    type: newFeedbackType,
                    rating: 5
                }),
                credentials: 'include'
            });

            if (res.ok) {
                const savedFeedback = await res.json();
                
                // Update with real ID
                setProject(current => {
                    const updated = { ...current };
                    const idx = updated.feedbacks.findIndex(f => f.id === tempId);
                    if (idx !== -1) {
                         updated.feedbacks[idx].id = savedFeedback.id;
                    }
                    return updated;
                });
            } else {
                const err = await res.json();
                // We should revert if failed
                setProject(originalProject);
                alert(`Failed to save feedback: ${err.message}`);
            }
        } catch (error) {
            console.error("Error submitting feedback:", error);
            setProject(originalProject);
            alert("An error occurred while submitting feedback.");
        }
    };

    if (!project) {
        return (
            <div className="project-details-container loading-container">
                <div className="loading-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                </div>
                <p className="loading-text">Loading Project Details...</p>
            </div>
        );
    }

    return (
        <div className="project-details-container">
            <Helmet>
                <title>{project.title} | MintraLib</title>
                <meta name="description" content={`MintraLib (Mintra) resources for ${project.title}.`} />
            </Helmet>
            <button className="back-btn" onClick={() => navigate(-1)}>
                <FiArrowLeft /> Back to Library
            </button>

            <div className="details-header">
                <div>
                    <h1 className="details-title">{project.title}</h1>
                    <div className="details-meta">
                        <span className="details-badge">Level {project.level}</span>
                    </div>
                </div>
            </div>

            <div className="project-layout-grid">
                <div className="project-main-content">
                    <div className="details-section description-box">
                <h2>Description</h2>
                <p className="description-text">{project.description}</p>
                
                {project.learning_objectives && project.learning_objectives.length > 0 && (
                    <div className="learning-objectives">
                        <h3><span className="accent-bar"></span>What You Will Learn</h3>
                        <ul>
                            {project.learning_objectives.map((objective, index) => (
                                <li key={index}>{objective}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="project-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`}
                    onClick={() => setActiveTab('resources')}
                >
                    Resources
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
                    onClick={() => setActiveTab('feedback')}
                >
                    Feedback
                </button>
            </div>

            {activeTab === 'resources' && (
            <div className="details-section">
                <div className="feedback-header">
                    <h2>Resources</h2>
                    <button className="add-feedback-btn" onClick={() => setShowResourceForm(!showResourceForm)}>
                        <FiPlus /> Add Resource
                    </button>
                </div>

                {showResourceForm && (
                     <div className="resource-form-card">
                        <input 
                            type="text" 
                            placeholder="Resource Title"
                            className="resource-input"
                            value={newResource.title}
                            onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                        />
                        <div className="form-row">
                            <select 
                                className="resource-select"
                                value={newResource.type}
                                onChange={(e) => setNewResource({...newResource, type: e.target.value})}
                            >
                                <option value="Docs">Docs</option>
                                <option value="Video">Video</option>
                                <option value="Guide">Guide</option>
                                <option value="Tool">Tool</option>
                                <option value="PDF">PDF</option>
                            </select>
                            <input 
                                type="text" 
                                placeholder="URL"
                                className="resource-input"
                                value={newResource.url}
                                onChange={(e) => setNewResource({...newResource, url: e.target.value})}
                            />
                        </div>
                        <div className="form-actions">
                            <button className="cancel-btn" onClick={() => setShowResourceForm(false)}>Cancel</button>
                            <button className="submit-btn" onClick={handleAddResource}>Submit</button>
                        </div>
                     </div>
                )}

                {project.resources && project.resources.length > 0 ? (
                    <div className="resources-grid">
                        {[...project.resources].sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0)).map((res, index) => {
                            const currentUserVote = res.userVote || 0;
                            return (
                            <div key={index} className="resource-card">
                                <div className="vote-section">
                                    <button 
                                        className={`vote-btn ${currentUserVote === 1 ? 'active-up' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const originalIndex = project.resources.findIndex(r => r.id === res.id);
                                            handleResourceVote(originalIndex, 1);
                                        }}
                                        title="Upvote"
                                    >
                                        <FiArrowUp size={24} />
                                    </button>
                                    <span className="vote-count" style={{ 
                                        color: currentUserVote === 1 ? 'var(--accent-primary)' : 
                                               currentUserVote === -1 ? '#ef4444' : 'inherit' 
                                    }}>
                                        {res.upvotes || 0}
                                    </span>
                                    <button 
                                        className={`vote-btn ${currentUserVote === -1 ? 'active-down' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const originalIndex = project.resources.findIndex(r => r.id === res.id);
                                            handleResourceVote(originalIndex, -1);
                                        }}
                                        title="Downvote"
                                    >
                                        <FiArrowDown size={24} />
                                    </button>
                                </div>
                                
                                <a href={res.url} target="_blank" rel="noopener noreferrer" className="resource-content-wrapper">
                                    <div className="resource-header">
                                        <div className="resource-user-info">
                                            <div className="mini-avatar">
                                                {res.userImage ? (
                                                    <img src={res.userImage} alt={res.user} className="user-avatar-small" />
                                                ) : (
                                                    res.user ? res.user[0].toUpperCase() : 'U'
                                                )}
                                            </div>
                                            <div className="mini-meta">
                                                <span className="mini-username">{res.user || 'Anonymous'}</span>
                                                <span className="mini-date">{res.date}</span>
                                            </div>
                                        </div>
                                        <span className="resource-type">{res.type}</span>
                                    </div>
                                    
                                    <h3 className="resource-title">{res.title}</h3>
                                    
                                    <div className="resource-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <FiExternalLink className="resource-link-icon" size={18} />
                                         {user && user.login === res.user && (
                                            <button 
                                                className="delete-btn" 
                                                style={{padding: 0}}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const originalIndex = project.resources.findIndex(r => r.id === res.id);
                                                    handleDeleteResource(originalIndex, res.id);
                                                }}
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </a>
                            </div>
                         );
                        })}
                    </div>
                ) : (
                    <p>No resources available for this project yet.</p>
                )}
            </div>
            )}

            {activeTab === 'feedback' && (
            <div className="details-section">
                <div className="feedback-header">
                    <h2>Feedbacks</h2>
                    <button className="add-feedback-btn" onClick={() => setShowFeedbackForm(!showFeedbackForm)}>
                        <FiPlus /> Write Feedback
                    </button>
                </div>

                {showFeedbackForm && (
                     <div className="resource-form-card">
                         <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <label style={{ color: 'var(--text-secondary)', marginRight: '1rem' }}>Feedback Type:</label>
                            <div className="feedback-type-selector">
                                {['Information', 'Problem', 'Edge Case'].map(type => (
                                    <button
                                        key={type}
                                        className={`type-btn ${newFeedbackType === type ? 'active' : ''} ${type.toLowerCase().replace(' ', '-')}`}
                                        onClick={() => setNewFeedbackType(type)}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                         </div>
                        <textarea
                            className="resource-input"
                            style={{ minHeight: '100px', resize: 'vertical' }}
                            placeholder="Share your experience, a tricky bug, or a helpful tip..."
                            value={newFeedbackText}
                            onChange={(e) => setNewFeedbackText(e.target.value)}
                        />
                        <div className="form-actions">
                            <button className="cancel-btn" onClick={() => setShowFeedbackForm(false)}>Cancel</button>
                            <button className="submit-btn" onClick={handleAddFeedback}>Post Feedback</button>
                        </div>
                     </div>
                )}
                
                <div className="feedback-filters">
                    {['All', 'Information', 'Problem', 'Edge Case'].map(filter => (
                        <button
                            key={filter}
                            className={`filter-pill ${activeFeedbackFilter === filter ? 'active' : ''}`}
                            onClick={() => setActiveFeedbackFilter(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                <div className="feedback-list">
                    {project.feedbacks && project.feedbacks.length > 0 ? (
                        project.feedbacks
                            .filter(fb => activeFeedbackFilter === 'All' || (fb.type || 'Information') === activeFeedbackFilter)
                            .sort((a, b) => {
                                if (activeFeedbackFilter === 'Edge Case') {
                                    return (b.votes || 0) - (a.votes || 0);
                                }
                                // Keep default order (likely date) for others
                                return 0;
                            })
                            .map((feedback, idx) => {
                                const currentUserVote = feedback.userVotes ? (feedback.userVotes[user ? user.login : "Anonymous"] || 0) : 0;
                                return (
                            <div key={idx} className={`feedback-card type-${(feedback.type || 'Information').toLowerCase().replace(' ', '-')}`}>
                                
                                {/* Vote Section for Edge Cases */}
                                {(feedback.type === 'Edge Case') && (
                                     <div className="vote-section feedback-vote">
                                        <button 
                                            className={`vote-btn ${currentUserVote === 1 ? 'active-up' : ''}`}
                                            onClick={() => handleFeedbackVote(feedback.id, 1)}
                                            title="Upvote"
                                        >
                                            <FiArrowUp size={20} />
                                        </button>
                                        <span className="vote-count" style={{ 
                                            color: currentUserVote === 1 ? 'var(--accent-primary)' : 
                                                currentUserVote === -1 ? '#ef4444' : 'inherit' 
                                        }}>
                                            {feedback.votes || 0}
                                        </span>
                                        <button 
                                            className={`vote-btn ${currentUserVote === -1 ? 'active-down' : ''}`}
                                            onClick={() => handleFeedbackVote(feedback.id, -1)}
                                            title="Downvote"
                                        >
                                            <FiArrowDown size={20} />
                                        </button>
                                     </div>
                                )}
                                <div className="feedback-content-wrapper" style={{ width: '100%' }}>
                                    <div className="feedback-main">
                                        <div className="feedback-user">
                                        {feedback.userImage ? (
                                            <img src={feedback.userImage} alt={feedback.user} className="user-avatar" style={{objectFit: 'cover'}} />
                                        ) : (
                                            <div className="user-avatar">{feedback.user[0].toUpperCase()}</div>
                                        )}
                                        <div className="user-info">
                                            <span className="username">{feedback.user}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="feedback-date">{feedback.date}</span>
                                                <span className={`feedback-type-badge ${(feedback.type || 'Information').toLowerCase().replace(' ', '-')}`}>
                                                    {feedback.type || 'Information'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="feedback-content">{feedback.comment}</p>
                                    
                                    <div className="feedback-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <button className="reply-btn" onClick={() => handleReplyClick(idx)}>
                                            <FiMessageCircle /> Reply
                                        </button>
                                        {user && user.login === feedback.user && (
                                            <button className="delete-btn" onClick={() => handleDeleteFeedback(feedback.id)}>
                                                <FiTrash2 size={14} /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                                 {/* Replies Section */}
                                {feedback.replies && feedback.replies.length > 0 && (
                                    <div className="replies-wrapper">
                                        <button 
                                            className="toggle-replies-btn" 
                                            onClick={() => toggleReplies(idx)}
                                        >
                                            {expandedFeedbacks[idx] ? <FiChevronUp /> : <FiChevronDown />}
                                            {expandedFeedbacks[idx] ? 'Hide Replies' : `Show ${feedback.replies.length} Replies`}
                                        </button>
                                        
                                        {expandedFeedbacks[idx] && (
                                            <div className="replies-container">
                                                {feedback.replies.map((reply, rIdx) => (
                                                    <div key={rIdx} className="reply-item">
                                                        <div className="reply-header">
                                                            <div className="reply-user-info">
                                                                {reply.userImage ? (
                                                                    <img src={reply.userImage} alt={reply.user} className="reply-avatar" />
                                                                ) : (
                                                                    <div className="reply-avatar-placeholder">
                                                                        {reply.user ? reply.user[0].toUpperCase() : 'A'}
                                                                    </div>
                                                                )}
                                                                <span className="reply-username">{reply.user}</span>
                                                            </div>
                                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                <span className="reply-date">{reply.date}</span>
                                                                <button 
                                                                    onClick={() => handleMentionReply(idx, reply.user)}
                                                                    title="Reply to user"
                                                                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                                >
                                                                    <FiMessageCircle size={14} />
                                                                </button>
                                                                {user && user.login === reply.user && (
                                                                    <button 
                                                                        className="delete-btn" 
                                                                        onClick={() => handleDeleteReply(idx, rIdx, reply.id)}
                                                                        title="Delete Reply"
                                                                    >
                                                                        <FiTrash2 size={12} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="reply-content">
                                                            {reply.comment.split(/(@\w+)/g).map((part, i) => 
                                                                part.startsWith('@') ? <span key={i} className="mention-tag">{part}</span> : part
                                                            )}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Reply Input */}
                                {replyingTo === idx && (
                                    <div className="reply-input-area">
                                        <input 
                                            type="text" 
                                            placeholder="Write a reply..." 
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            autoFocus
                                        />
                                        <button className="send-reply-btn" onClick={() => submitReply(idx)}>
                                            <FiSend />
                                        </button>
                                    </div>
                                )}
                                </div>
                            </div>
                            );
                        })
                    ) : (
                        <p>No feedbacks yet.</p>
                    )}
                </div>
            </div>
            )}
                </div>
                
                <aside className="project-sidebar">
                    <div className="sidebar-section">
                        <div className="user-list-tabs">
                            <button 
                                className={`tab-btn ${activeUserTab === 'completed' ? 'active' : ''}`}
                                onClick={() => setActiveUserTab('completed')}
                            >
                                Completed By
                            </button>
                            <button 
                                className={`tab-btn ${activeUserTab === 'inprogress' ? 'active' : ''}`}
                                onClick={() => setActiveUserTab('inprogress')}
                            >
                                In Progress
                            </button>
                        </div>
                        
                        <div className="completed-list">
                            {activeUserTab === 'completed' ? (
                                project.completedBy && project.completedBy.length > 0 ? (
                                    project.completedBy.map((user, idx) => (
                                        <div key={idx} className="completed-user-item">
                                            {user.userImage ? (
                                                <img 
                                                    src={user.userImage} 
                                                    alt={user.user}
                                                    className="user-avatar-circle"
                                                    style={{ borderRadius: '50%', objectFit: 'cover', padding: 0 }} 
                                                />
                                            ) : (
                                                <div 
                                                    className="user-avatar-circle"
                                                    style={{ backgroundColor: user.avatar_color || '#3b82f6' }}
                                                >
                                                    {user.user[0].toUpperCase()}
                                                </div>
                                            )}
                                            <div className="user-details">
                                                <span className="user-name">{user.user}</span>
                                                <span className="completion-date">
                                                    {user.mark !== undefined ? `Mark: ${user.mark} - ` : ''} 
                                                    {user.date}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-data-text">Be the first to finish!</p>
                                )
                            ) : (
                                project.inProgressBy && project.inProgressBy.length > 0 ? (
                                    project.inProgressBy.map((user, idx) => (
                                        <div key={idx} className="completed-user-item">
                                            {user.userImage ? (
                                                <img 
                                                    src={user.userImage} 
                                                    alt={user.user}
                                                    className="user-avatar-circle"
                                                    style={{ borderRadius: '50%', objectFit: 'cover', padding: 0, border: '2px dashed #fff' }} 
                                                />
                                            ) : (
                                                <div 
                                                    className="user-avatar-circle"
                                                    style={{ backgroundColor: user.avatar_color || '#3b82f6', border: '2px dashed #fff' }}
                                                >
                                                    {user.user[0].toUpperCase()}
                                                </div>
                                            )}
                                            <div className="user-details">
                                                <span className="user-name">{user.user}</span>
                                                <span className="completion-date">{user.date}</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-data-text">No one is currently working on this.</p>
                                )
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};



export default ProjectDetails;
